import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, IconButton, TextField, MenuItem, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Snackbar,
  ToggleButtonGroup, ToggleButton, Autocomplete, CircularProgress, Chip, Alert,
} from '@mui/material';
import {
  Add, Refresh, ArrowBack, DeleteOutline, ReceiptLongOutlined,
  PendingActionsOutlined, AccountBalanceOutlined, SavingsOutlined, UndoOutlined,
  OpenInNewOutlined, SendOutlined, VerifiedOutlined, PostAddOutlined, DescriptionOutlined,
} from '@mui/icons-material';
import { notesService, NoteKind, Note, NotePreview, NotesDashboard, NoteLineInput } from '../../services/notesService';
import { customerService } from '../../services/customerService';
import { procurementService } from '../../services/procurementService';
import invoiceService from '../../services/invoiceService';
import { formatCurrency, formatIndianNumber, numberToWords } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  StatCard, TableSurface, headerCellSx, bodyRowSx, StatusDot, StatusTone, TypePill,
  TableEmptyState, ErrorState,
} from '../../components/common/pageKit';

// ---------------- shared helpers ----------------
const num = (v: any) => Number(v) || 0;
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const dt = (v?: string | null) => (v ? new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const d = (v?: string | null) => (v ? new Date(v).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—');

const STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'neutral', SUBMITTED: 'info', APPROVED: 'warning', POSTED: 'success', REVERSED: 'error', CANCELLED: 'error',
};

const NOTE_TYPES = ['RATE_DIFFERENCE', 'RETURN', 'DISCOUNT', 'SALES_CREDIT', 'PURCHASE_DEBIT', 'OTHER'];
const typeLabel = (t?: string | null) => (t ? t.replace(/_/g, ' ') : '—');

interface FormLine {
  description: string; hsnCode: string; quantity: string; unitPrice: string;
  cgstAmount: string; sgstAmount: string; igstAmount: string; tdsAmount: string; taxRate: string;
}
const blankLine = (): FormLine => ({ description: '', hsnCode: '', quantity: '1', unitPrice: '', cgstAmount: '', sgstAmount: '', igstAmount: '', tdsAmount: '', taxRate: '' });

const lineTaxable = (l: FormLine) => round2(num(l.quantity) * num(l.unitPrice));
const lineTotal = (l: FormLine) => round2(lineTaxable(l) + num(l.cgstAmount) + num(l.sgstAmount) + num(l.igstAmount));

// =========================================================================
// MAIN
// =========================================================================
const CreditDebitNotesPage: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const [kind, setKind] = useState<NoteKind>('credit');
  const [view, setView] = useState<'list' | 'create' | 'detail'>('list');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [dashboard, setDashboard] = useState<NotesDashboard | null>(null);
  const [rows, setRows] = useState<Note[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const loadDashboard = useCallback(() => {
    notesService.dashboard().then(setDashboard).catch(() => {});
  }, []);
  const loadList = useCallback(() => {
    setLoading(true); setError(null);
    notesService.list(kind, statusFilter ? { status: statusFilter } : {})
      .then(setRows).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [kind, statusFilter]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { loadList(); }, [loadList]);

  const kd = dashboard ? dashboard[kind] : null;
  const isCredit = kind === 'credit';
  const partyLabel = isCredit ? 'Customer' : 'Vendor';

  const kpis = kd ? [
    { title: `${isCredit ? 'Credit' : 'Debit'} Notes (MTD)`, value: kd.periodCount, sub: formatIndianNumber(kd.periodValue), icon: <ReceiptLongOutlined />, color: c.accent },
    { title: 'Pending Approval', value: kd.pendingApproval, icon: <PendingActionsOutlined />, color: c.warn, onClick: () => setStatusFilter('SUBMITTED') },
    { title: 'Pending Posting', value: kd.pendingPosting, icon: <PostAddOutlined />, color: c.cyan, onClick: () => setStatusFilter('DRAFT') },
    { title: 'Tax Impact (posted)', value: formatIndianNumber(kd.taxImpact), icon: <AccountBalanceOutlined />, color: c.purple },
    { title: 'Total Adjusted', value: formatIndianNumber(kd.totalValue), icon: <SavingsOutlined />, color: c.good },
    { title: 'Reversed', value: kd.reversed, icon: <UndoOutlined />, color: c.bad, onClick: () => setStatusFilter('REVERSED') },
  ] : [];

  const openDetail = (id: number) => { setSelectedId(id); setView('detail'); };
  const backToList = () => { setView('list'); setSelectedId(null); loadList(); loadDashboard(); };

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }} flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Credit &amp; Debit Notes</Typography>
          <Typography variant="body2" sx={{ color: c.sub, mt: 0.5 }}>
            Adjustment workspace — sales returns, rate differences &amp; discounts, GL-posted and applied to the original invoice.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            size="small" exclusive value={kind}
            onChange={(_, v) => { if (v) { setKind(v); setStatusFilter(''); setView('list'); } }}
          >
            <ToggleButton value="credit" sx={{ textTransform: 'none', px: 2 }}>Credit (AR)</ToggleButton>
            <ToggleButton value="debit" sx={{ textTransform: 'none', px: 2 }}>Debit (AP)</ToggleButton>
          </ToggleButtonGroup>
          {view === 'list' && (
            <>
              <Tooltip title="Refresh"><IconButton onClick={() => { loadList(); loadDashboard(); }}><Refresh /></IconButton></Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={() => setView('create')}>New {isCredit ? 'Credit' : 'Debit'} Note</Button>
            </>
          )}
        </Stack>
      </Stack>

      {view === 'list' && (
        <>
          {/* KPI strip */}
          <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
            {(kpis.length ? kpis : Array.from({ length: 6 })).map((k: any, i) => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i}>
                {k ? (
                  <StatCard title={k.title} value={k.value} icon={k.icon} color={k.color} onClick={k.onClick}
                    footer={k.sub ? <Typography variant="caption" sx={{ color: c.sub }}>{k.sub} value</Typography> : undefined} />
                ) : (
                  <Paper sx={{ ...panelSx, p: 2, height: 108, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={20} /></Paper>
                )}
              </Grid>
            ))}
          </Grid>

          {/* Filters */}
          <Paper sx={{ ...panelSx, p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 180 }}>
                <MenuItem value="">All statuses</MenuItem>
                {['DRAFT', 'SUBMITTED', 'APPROVED', 'POSTED', 'REVERSED', 'CANCELLED'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="body2" sx={{ color: c.sub }}>{rows.length} note{rows.length === 1 ? '' : 's'}</Typography>
            </Stack>
          </Paper>

          {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={loadList} /></Box>}

          {/* Table */}
          <TableSurface>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Note #</TableCell>
                    <TableCell sx={headerCellSx}>{partyLabel}</TableCell>
                    <TableCell sx={headerCellSx}>Type</TableCell>
                    <TableCell sx={headerCellSx}>Original Invoice</TableCell>
                    <TableCell sx={headerCellSx}>Date</TableCell>
                    <TableCell sx={headerCellSx} align="right">Taxable</TableCell>
                    <TableCell sx={headerCellSx} align="right">Tax</TableCell>
                    <TableCell sx={headerCellSx} align="right">Total</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, border: 0 }}><CircularProgress size={26} /></TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} sx={{ border: 0 }}><TableEmptyState title="No notes yet" hint={`Create a ${isCredit ? 'credit' : 'debit'} note to adjust an invoice.`} /></TableCell></TableRow>
                  ) : rows.map((r) => {
                    const tax = num(r.cgstAmount) + num(r.sgstAmount) + num(r.igstAmount);
                    return (
                      <TableRow key={r.id} hover sx={{ ...(bodyRowSx as any), cursor: 'pointer' }} onClick={() => openDetail(r.id)}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: c.accent }}>{r.cnNumber || r.dnNumber}</TableCell>
                        <TableCell>{r.party_name || '—'}</TableCell>
                        <TableCell>{r.noteType ? <TypePill label={typeLabel(r.noteType)} tint={c.purple} /> : '—'}</TableCell>
                        <TableCell>
                          {r.invoice_number
                            ? <Chip size="small" variant="outlined" label={r.invoice_number} onClick={(e) => { e.stopPropagation(); openDetail(r.id); }} sx={{ fontFamily: 'monospace' }} />
                            : <Typography variant="caption" sx={{ color: c.sub }}>Unlinked</Typography>}
                        </TableCell>
                        <TableCell>{d(r.cnDate || r.dnDate)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(num(r.subtotal))}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(tax)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatCurrency(num(r.totalAmount))}</TableCell>
                        <TableCell><StatusDot label={r.status} tone={STATUS_TONE[r.status] || 'neutral'} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </>
      )}

      {view === 'create' && (
        <CreateNoteForm kind={kind} colors={c} onCancel={backToList}
          onSaved={(msg) => { setToast(msg); backToList(); }} />
      )}

      {view === 'detail' && selectedId != null && (
        <NoteDetail kind={kind} id={selectedId} colors={c} navigate={navigate}
          onBack={backToList} onAction={(msg) => { setToast(msg); loadDashboard(); }} />
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

// =========================================================================
// CREATE FORM
// =========================================================================
const CreateNoteForm: React.FC<{ kind: NoteKind; colors: any; onCancel: () => void; onSaved: (msg: string) => void }> = ({ kind, colors: c, onCancel, onSaved }) => {
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;
  const isCredit = kind === 'credit';
  const today = new Date().toISOString().slice(0, 10);

  const [parties, setParties] = useState<any[]>([]);
  const [party, setParty] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invoice, setInvoice] = useState<any>(null);
  const [invLoading, setInvLoading] = useState(false);

  const [header, setHeader] = useState({ noteType: isCredit ? 'SALES_CREDIT' : 'PURCHASE_DEBIT', docDate: today, postingDate: today, reference: '', reason: '', currency: 'INR' });
  const [lines, setLines] = useState<FormLine[]>([blankLine()]);
  const [preview, setPreview] = useState<NotePreview | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [saving, setSaving] = useState<'' | 'draft' | 'post'>('');
  const [err, setErr] = useState<string | null>(null);

  // Load parties
  useEffect(() => {
    setParty(null); setInvoice(null); setInvoices([]);
    if (isCredit) customerService.getCustomers().then((r: any) => setParties(Array.isArray(r) ? r : (r.customers || []))).catch(() => {});
    else procurementService.getVendors().then((r: any) => setParties(r.vendors || r || [])).catch(() => {});
  }, [isCredit]);

  // Load invoices for the selected party
  useEffect(() => {
    if (!party) { setInvoices([]); return; }
    setInvLoading(true); setInvoice(null);
    const pid = party.id;
    if (isCredit) {
      invoiceService.getInvoices({ customerId: Number(pid) })
        .then((r: any) => setInvoices(Array.isArray(r) ? r : (r.invoices || [])))
        .catch(() => setInvoices([])).finally(() => setInvLoading(false));
    } else {
      procurementService.getPurchaseInvoices({ vendorId: pid })
        .then((r: any) => setInvoices(r.invoices || r || []))
        .catch(() => setInvoices([])).finally(() => setInvLoading(false));
    }
  }, [party, isCredit]);

  const totals = useMemo(() => {
    const subtotal = round2(lines.reduce((s, l) => s + lineTaxable(l), 0));
    const cgst = round2(lines.reduce((s, l) => s + num(l.cgstAmount), 0));
    const sgst = round2(lines.reduce((s, l) => s + num(l.sgstAmount), 0));
    const igst = round2(lines.reduce((s, l) => s + num(l.igstAmount), 0));
    const tds = round2(lines.reduce((s, l) => s + num(l.tdsAmount), 0));
    const total = round2(subtotal + cgst + sgst + igst);
    return { subtotal, cgst, sgst, igst, tds, total };
  }, [lines]);

  const toLineInputs = (): NoteLineInput[] => lines
    .filter((l) => num(l.quantity) > 0 && num(l.unitPrice) > 0)
    .map((l) => ({
      description: l.description || undefined, hsnCode: l.hsnCode || undefined,
      quantity: num(l.quantity), unitPrice: num(l.unitPrice),
      cgstAmount: num(l.cgstAmount), sgstAmount: num(l.sgstAmount), igstAmount: num(l.igstAmount),
      tdsAmount: num(l.tdsAmount), taxRate: l.taxRate ? num(l.taxRate) : null,
    }));

  // Live preview (debounced) whenever lines change and there is a positive total.
  useEffect(() => {
    const li = toLineInputs();
    if (!li.length) { setPreview(null); setPreviewErr(null); return; }
    const t = setTimeout(() => {
      notesService.preview(kind, { lines: li, reference: header.reference })
        .then((p) => { setPreview(p); setPreviewErr(null); })
        .catch((e) => { setPreview(null); setPreviewErr(e.message); });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(lines), kind]);

  const setLine = (i: number, patch: Partial<FormLine>) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, blankLine()]);
  const removeLine = (i: number) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const buildBody = (post: boolean) => {
    const li = toLineInputs();
    const body: any = { noteType: header.noteType, reference: header.reference || undefined, reason: header.reason || undefined, postingDate: header.postingDate, lines: li, post };
    if (isCredit) { body.customerId = Number(party.id); body.cnDate = header.docDate; if (invoice) body.invoiceId = Number(invoice.id); }
    else { body.vendorId = Number(party.id); body.dnDate = header.docDate; if (invoice) body.purchaseInvoiceId = Number(invoice.id); }
    return body;
  };

  const save = async (post: boolean) => {
    setErr(null);
    if (!party) { setErr(`Select a ${isCredit ? 'customer' : 'vendor'}.`); return; }
    if (!toLineInputs().length) { setErr('Add at least one line with quantity and rate.'); return; }
    if (post && preview && !preview.balanced) { setErr('The entry is not balanced — cannot post.'); return; }
    setSaving(post ? 'post' : 'draft');
    try {
      await notesService.create(kind, buildBody(post));
      onSaved(post ? 'Note posted to the ledger' : 'Draft note saved');
    } catch (e: any) { setErr(e.message); } finally { setSaving(''); }
  };

  const invLabel = (o: any) => isCredit
    ? `${o.invoiceNumber} · ${formatCurrency(num(o.totalAmount))}`
    : `${o.invoice_no} · ${formatCurrency(num(o.net_amount ?? o.total_amount))}`;

  const balanced = preview?.balanced;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={onCancel}><ArrowBack /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>New {isCredit ? 'Credit' : 'Debit'} Note</Typography>
        <TypePill label={isCredit ? 'AR' : 'AP'} tint={c.accent} />
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}

      <Grid container spacing={2}>
        {/* LEFT: form */}
        <Grid size={{ xs: 12, lg: 8 }}>
          {/* Header */}
          <Paper sx={{ ...panelSx, p: 2.5, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Document</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Note type" value={header.noteType} onChange={(e) => setHeader({ ...header, noteType: e.target.value })}>
                  {NOTE_TYPES.map((t) => <MenuItem key={t} value={t}>{typeLabel(t)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  size="small" options={parties} value={party} onChange={(_, v) => setParty(v)}
                  getOptionLabel={(o: any) => o?.company_name || o?.name || ''}
                  isOptionEqualToValue={(a: any, b: any) => a.id === b.id}
                  renderInput={(p) => <TextField {...p} label={`${isCredit ? 'Customer' : 'Vendor'} *`} />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  size="small" options={invoices} value={invoice} onChange={(_, v) => setInvoice(v)}
                  disabled={!party} loading={invLoading}
                  getOptionLabel={invLabel}
                  isOptionEqualToValue={(a: any, b: any) => a.id === b.id}
                  renderInput={(p) => <TextField {...p} label="Original invoice (optional)" helperText={party ? `${invoices.length} invoice(s)` : 'Select a party first'} />}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField fullWidth size="small" type="date" label="Document date" InputLabelProps={{ shrink: true }} value={header.docDate} onChange={(e) => setHeader({ ...header, docDate: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField fullWidth size="small" type="date" label="Posting date" InputLabelProps={{ shrink: true }} value={header.postingDate} onChange={(e) => setHeader({ ...header, postingDate: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Reference" value={header.reference} onChange={(e) => setHeader({ ...header, reference: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Reason" value={header.reason} onChange={(e) => setHeader({ ...header, reason: e.target.value })} />
              </Grid>
            </Grid>
          </Paper>

          {/* Lines */}
          <Paper sx={{ ...panelSx, p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Lines</Typography>
              <Button size="small" startIcon={<Add />} onClick={addLine}>Add line</Button>
            </Stack>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Description', 'HSN', 'Qty', 'Rate', 'CGST', 'SGST', 'IGST', 'TDS', 'Line total', ''].map((h, i) => (
                      <TableCell key={i} sx={{ ...headerCellSx, py: 1 }} align={['Qty', 'Rate', 'CGST', 'SGST', 'IGST', 'TDS', 'Line total'].includes(h) ? 'right' : 'left'}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ minWidth: 160 }}><TextField variant="standard" fullWidth placeholder="Item / reason" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} /></TableCell>
                      <TableCell sx={{ width: 80 }}><TextField variant="standard" value={l.hsnCode} onChange={(e) => setLine(i, { hsnCode: e.target.value })} /></TableCell>
                      <TableCell sx={{ width: 70 }}><TextField variant="standard" type="number" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} /></TableCell>
                      <TableCell sx={{ width: 90 }}><TextField variant="standard" type="number" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} /></TableCell>
                      <TableCell sx={{ width: 80 }}><TextField variant="standard" type="number" value={l.cgstAmount} onChange={(e) => setLine(i, { cgstAmount: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} /></TableCell>
                      <TableCell sx={{ width: 80 }}><TextField variant="standard" type="number" value={l.sgstAmount} onChange={(e) => setLine(i, { sgstAmount: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} /></TableCell>
                      <TableCell sx={{ width: 80 }}><TextField variant="standard" type="number" value={l.igstAmount} onChange={(e) => setLine(i, { igstAmount: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} /></TableCell>
                      <TableCell sx={{ width: 80 }}><TextField variant="standard" type="number" value={l.tdsAmount} onChange={(e) => setLine(i, { tdsAmount: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} /></TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(lineTotal(l))}</TableCell>
                      <TableCell sx={{ width: 36 }}><IconButton size="small" disabled={lines.length <= 1} onClick={() => removeLine(i)}><DeleteOutline fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Totals strip */}
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={3} justifyContent="flex-end" flexWrap="wrap">
              <TotalItem label="Taxable" value={totals.subtotal} c={c} />
              <TotalItem label="CGST" value={totals.cgst} c={c} />
              <TotalItem label="SGST" value={totals.sgst} c={c} />
              <TotalItem label="IGST" value={totals.igst} c={c} />
              <TotalItem label="TDS" value={totals.tds} c={c} />
              <TotalItem label="Total" value={totals.total} c={c} strong />
            </Stack>
          </Paper>
        </Grid>

        {/* RIGHT: impact + document preview */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ ...panelSx, p: 2.5, mb: 2, position: { lg: 'sticky' as const }, top: 16 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <AccountBalanceOutlined fontSize="small" sx={{ color: c.accent }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Accounting impact</Typography>
            </Stack>
            {previewErr && <Alert severity="warning" sx={{ mb: 1 }}>{previewErr}</Alert>}
            {!preview && !previewErr && <Typography variant="body2" sx={{ color: c.sub }}>Add a line to preview the journal entry.</Typography>}
            {preview && (
              <>
                <Table size="small">
                  <TableBody>
                    {preview.legs.map((leg, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ border: 0, py: 0.5 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: c.accent, fontWeight: 700 }}>{leg.accountCode}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: c.sub }}>{leg.accountName}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ border: 0, py: 0.5, fontVariantNumeric: 'tabular-nums' }}>{leg.debit ? formatCurrency(leg.debit) : ''}</TableCell>
                        <TableCell align="right" sx={{ border: 0, py: 0.5, fontVariantNumeric: 'tabular-nums' }}>{leg.credit ? formatCurrency(leg.credit) : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: c.sub }}>Debit {formatCurrency(preview.totalDebit)} · Credit {formatCurrency(preview.totalCredit)}</Typography>
                  <Chip size="small" label={balanced ? 'Balanced' : `Diff ${formatCurrency(preview.difference)}`}
                    sx={{ bgcolor: balanced ? `${c.good}1f` : `${c.bad}1f`, color: balanced ? c.good : c.bad, fontWeight: 700 }} />
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: c.sub }}>GST adjustment: {formatCurrency(preview.totals.gstDelta)}</Typography>
              </>
            )}
          </Paper>

          {/* Document preview */}
          <Paper sx={{ ...panelSx, p: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <DescriptionOutlined fontSize="small" sx={{ color: c.purple }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Document preview</Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: c.sub }}>{isCredit ? 'CREDIT NOTE' : 'DEBIT NOTE'}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{party?.company_name || party?.name || `[${partyLabel(isCredit)}]`}</Typography>
            {invoice && <Typography variant="caption" sx={{ color: c.sub, display: 'block' }}>Against {isCredit ? invoice.invoiceNumber : invoice.invoice_no}</Typography>}
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.5}>
              <RowKV k="Taxable" v={formatCurrency(totals.subtotal)} c={c} />
              <RowKV k="Tax" v={formatCurrency(totals.cgst + totals.sgst + totals.igst)} c={c} />
              <RowKV k="Total" v={formatCurrency(totals.total)} c={c} strong />
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: c.sub }}>
              {totals.total > 0 ? numberToWords(totals.total) : ''}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Actions */}
      <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2.5 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="outlined" disabled={!!saving} onClick={() => save(false)} startIcon={saving === 'draft' ? <CircularProgress size={16} /> : undefined}>Save draft</Button>
        <Button variant="contained" disabled={!!saving || (preview ? !preview.balanced : false)} onClick={() => save(true)} startIcon={saving === 'post' ? <CircularProgress size={16} /> : <PostAddOutlined />}>Post note</Button>
      </Stack>
    </Box>
  );
};

const partyLabel = (isCredit: boolean) => (isCredit ? 'Customer' : 'Vendor');

const TotalItem: React.FC<{ label: string; value: number; c: any; strong?: boolean }> = ({ label, value, c, strong }) => (
  <Box sx={{ textAlign: 'right' }}>
    <Typography variant="caption" sx={{ color: c.sub, display: 'block' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: strong ? 800 : 600, fontVariantNumeric: 'tabular-nums', color: strong ? c.text : undefined }}>{formatCurrency(value)}</Typography>
  </Box>
);

const RowKV: React.FC<{ k: string; v: string; c: any; strong?: boolean }> = ({ k, v, c, strong }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" sx={{ color: c.sub }}>{k}</Typography>
    <Typography variant="body2" sx={{ fontWeight: strong ? 800 : 600, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
  </Stack>
);

// =========================================================================
// DETAIL / 360
// =========================================================================
const NoteDetail: React.FC<{ kind: NoteKind; id: number; colors: any; navigate: any; onBack: () => void; onAction: (msg: string) => void }> = ({ kind, id, colors: c, navigate, onBack, onAction }) => {
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;
  const isCredit = kind === 'credit';

  const [note, setNote] = useState<Note | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    setLoading(true); setError(null);
    notesService.get(kind, id).then(setNote).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [kind, id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<Note>, msg: string) => {
    setBusy(msg);
    try { const updated = await fn(); setNote(updated); onAction(msg); }
    catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error || !note) return <Box><Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>Back</Button><ErrorState message={error || 'Not found'} onRetry={load} /></Box>;

  const number = note.cnNumber || note.dnNumber;
  const tax = num(note.cgstAmount) + num(note.sgstAmount) + num(note.igstAmount);

  // Lifecycle timeline
  const stages = [
    { key: 'created', label: 'Draft created', at: note.createdAt, done: true },
    { key: 'submitted', label: 'Submitted for approval', at: note.submittedAt, done: !!note.submittedAt },
    { key: 'approved', label: 'Approved', at: note.approvedAt, done: !!note.approvedAt },
    { key: 'posted', label: 'Posted to ledger', at: (note.status === 'POSTED' || note.status === 'REVERSED') ? (note.approvedAt || note.createdAt) : null, done: note.status === 'POSTED' || note.status === 'REVERSED' },
    { key: 'reversed', label: 'Reversed', at: note.reversedAt, done: !!note.reversedAt },
  ].filter((s) => s.key !== 'reversed' || s.done);

  const actions: React.ReactNode[] = [];
  if (note.status === 'DRAFT') {
    actions.push(<Button key="s" size="small" variant="outlined" startIcon={<SendOutlined />} disabled={!!busy} onClick={() => act(() => notesService.submit(kind, id), 'Submitted for approval')}>Submit</Button>);
    actions.push(<Button key="p" size="small" variant="contained" startIcon={<PostAddOutlined />} disabled={!!busy} onClick={() => act(() => notesService.post(kind, id), 'Posted')}>Post</Button>);
  }
  if (note.status === 'SUBMITTED') actions.push(<Button key="a" size="small" variant="contained" startIcon={<VerifiedOutlined />} disabled={!!busy} onClick={() => act(() => notesService.approve(kind, id), 'Approved')}>Approve</Button>);
  if (note.status === 'APPROVED') actions.push(<Button key="p2" size="small" variant="contained" startIcon={<PostAddOutlined />} disabled={!!busy} onClick={() => act(() => notesService.post(kind, id), 'Posted')}>Post</Button>);
  if (note.status === 'POSTED') actions.push(<Button key="r" size="small" variant="outlined" color="error" startIcon={<UndoOutlined />} disabled={!!busy} onClick={() => act(() => notesService.reverse(kind, id, 'Reversed from workspace'), 'Reversed')}>Reverse</Button>);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap">
        <IconButton onClick={onBack}><ArrowBack /></IconButton>
        <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 800 }}>{number}</Typography>
        <StatusDot label={note.status} tone={STATUS_TONE[note.status] || 'neutral'} />
        {note.noteType && <TypePill label={typeLabel(note.noteType)} tint={c.purple} />}
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1}>{actions}</Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Summary */}
          <Paper sx={{ ...panelSx, p: 2.5, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}><Field label={isCredit ? 'Customer' : 'Vendor'} value={note.party_name} c={c} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label="GSTIN" value={note.party_gstin} c={c} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label="Document date" value={d(note.cnDate || note.dnDate)} c={c} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label="Posting date" value={d(note.postingDate)} c={c} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" sx={{ color: c.sub, display: 'block' }}>Original invoice</Typography>
                {note.invoice_number
                  ? <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{note.invoice_number}</Typography>
                  : <Typography variant="body2" sx={{ color: c.sub }}>Unlinked</Typography>}
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" sx={{ color: c.sub, display: 'block' }}>Journal entry</Typography>
                {note.journalEntryId
                  ? <Button size="small" endIcon={<OpenInNewOutlined fontSize="small" />} sx={{ p: 0, minWidth: 0, fontFamily: 'monospace' }} onClick={() => navigate(`/finance/journal/${note.journalEntryId}`)}>{note.journal_entry_number || `#${note.journalEntryId}`}</Button>
                  : <Typography variant="body2" sx={{ color: c.sub }}>Not posted</Typography>}
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label="Reference" value={note.reference} c={c} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><Field label="Applied to invoice" value={formatCurrency(num(note.appliedAmount))} c={c} /></Grid>
              {note.reason && <Grid size={{ xs: 12 }}><Field label="Reason" value={note.reason} c={c} /></Grid>}
              {note.reversal_entry_number && <Grid size={{ xs: 12 }}>
                <Typography variant="caption" sx={{ color: c.sub, display: 'block' }}>Reversed by</Typography>
                <Button size="small" endIcon={<OpenInNewOutlined fontSize="small" />} sx={{ p: 0, minWidth: 0, fontFamily: 'monospace' }} onClick={() => navigate(`/finance/journal/${note.reversalJeId}`)}>{note.reversal_entry_number}</Button>
              </Grid>}
            </Grid>
          </Paper>

          {/* Lines */}
          <Paper sx={{ ...panelSx, p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Description', 'HSN', 'Qty', 'Rate', 'CGST', 'SGST', 'IGST', 'TDS', 'Total'].map((h, i) => (
                      <TableCell key={i} sx={headerCellSx} align={i >= 2 ? 'right' : 'left'}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(note.items || []).map((it) => (
                    <TableRow key={it.id}>
                      <TableCell>{it.description || '—'}</TableCell>
                      <TableCell>{it.hsnCode || '—'}</TableCell>
                      <TableCell align="right">{num(it.quantity)}</TableCell>
                      <TableCell align="right">{formatCurrency(num(it.unitPrice))}</TableCell>
                      <TableCell align="right">{formatCurrency(num(it.cgstAmount))}</TableCell>
                      <TableCell align="right">{formatCurrency(num(it.sgstAmount))}</TableCell>
                      <TableCell align="right">{formatCurrency(num(it.igstAmount))}</TableCell>
                      <TableCell align="right">{formatCurrency(num(it.tdsAmount))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(num(it.lineTotal))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <Stack direction="row" spacing={3} justifyContent="flex-end" sx={{ p: 2 }} flexWrap="wrap">
              <TotalItem label="Taxable" value={num(note.subtotal)} c={c} />
              <TotalItem label="Tax" value={tax} c={c} />
              <TotalItem label="Total" value={num(note.totalAmount)} c={c} strong />
            </Stack>
          </Paper>
        </Grid>

        {/* Right rail: lifecycle */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ ...panelSx, p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Lifecycle</Typography>
            <Stack>
              {stages.map((s, i) => (
                <Stack key={s.key} direction="row" spacing={1.5}>
                  <Stack alignItems="center">
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.done ? (s.key === 'reversed' ? c.bad : c.good) : c.line, mt: 0.5 }} />
                    {i < stages.length - 1 && <Box sx={{ width: 2, flexGrow: 1, minHeight: 26, bgcolor: c.line }} />}
                  </Stack>
                  <Box sx={{ pb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: s.done ? c.text : c.sub }}>{s.label}</Typography>
                    <Typography variant="caption" sx={{ color: c.sub }}>{s.done ? dt(s.at) : 'Pending'}</Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const Field: React.FC<{ label: string; value?: any; c: any }> = ({ label, value, c }) => (
  <Box>
    <Typography variant="caption" sx={{ color: c.sub, display: 'block' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value || '—'}</Typography>
  </Box>
);

export default CreditDebitNotesPage;
